/**
 * How the reader lays out a passage.
 *
 * Four modes rather than a side-by-side switch, because they answer four
 * different needs and a boolean can only answer one:
 *
 * - **Side by side** — studying: the Arabic and its translation in step.
 * - **Top and bottom** — the same pairing where a column would be too narrow,
 *   which on a phone is always.
 * - **Translation** — reading to understand, with nothing in the way.
 * - **Arabic** — reading or reciting the original, which is what someone does
 *   who followed the khutbah in the masjid and wants the text itself.
 */

export const VIEW_MODES = ['side-by-side', 'stacked', 'translation', 'arabic'] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

export const VIEW_LABELS: Record<ViewMode, string> = {
  'side-by-side': 'Side by side',
  stacked: 'Top and bottom',
  // "Translation" and "Arabic" alone read as a choice of LANGUAGE sitting
  // next to the language picker, which is how a reader came to report seeing
  // "only Arabic and English" in the menu. "only" makes them layouts.
  translation: 'Translation only',
  arabic: 'Arabic only',
};

/** Short forms for the phone toolbar, where the full labels will not fit. */
export const VIEW_SHORT: Record<ViewMode, string> = {
  'side-by-side': 'Both',
  stacked: 'Stacked',
  // "Translation" and "Arabic" alone read as a choice of LANGUAGE sitting
  // next to the language picker, which is how a reader came to report seeing
  // "only Arabic and English" in the menu. "only" makes them layouts.
  translation: 'Translation only',
  arabic: 'Arabic only',
};

export function isViewMode(value: unknown): value is ViewMode {
  return typeof value === 'string' && (VIEW_MODES as readonly string[]).includes(value);
}

/**
 * Side by side needs two columns, and a phone has room for one. Rather than
 * disable the mode there, it falls back to stacked — the reader keeps the
 * setting they chose on a laptop and still gets something readable on a
 * phone, instead of a control that silently does nothing.
 */
export function effectiveMode(mode: ViewMode, wide: boolean): ViewMode {
  return mode === 'side-by-side' && !wide ? 'stacked' : mode;
}

export const SHOWS_ARABIC: Record<ViewMode, boolean> = {
  'side-by-side': true,
  stacked: true,
  translation: false,
  arabic: true,
};

export const SHOWS_TRANSLATION: Record<ViewMode, boolean> = {
  'side-by-side': true,
  stacked: true,
  translation: true,
  arabic: false,
};
